/**
 * Sticky weighted A/B bucketing on the client.
 *
 * Picks a variant by weight, persists it in sessionStorage so it stays
 * stable for the rest of the tab, and merges the bucket into the URL
 * `experiments` param so usePageViewTracking and order-form submits
 * forward it to the backend (campaign_page_view_experiments and
 * order_campaign_experiments respectively).
 *
 * Mirrors the bucketing semantics of HasStackableExperiments (PHP) for
 * experiments that live entirely on the frontend.
 *
 * Resolution order:
 *   1. If the URL already carries `name:variant` for this experiment, honor it
 *      (allows external campaigns to force a bucket).
 *   2. Else reuse the sessionStorage-saved bucket if still valid.
 *   3. Else pick a fresh weighted bucket and persist it.
 *
 * @param {string} name Experiment name (must match what reads the join table)
 * @param {Object<string, number>} weights variant_key -> weight (1:1 = 50/50)
 * @returns {{ variant: string }}
 */
export function useExperiment(name, weights) {
    const allowed = Object.keys(weights);
    if (allowed.length === 0) {
        return { variant: '' };
    }

    const params = new URLSearchParams(window.location.search);
    const existing = params.get('experiments') || '';

    const fromUrl = variantFromUrlTags(existing, name, allowed);
    if (fromUrl !== null) {
        return { variant: fromUrl };
    }

    const storageKey = `experiment_${name}_variant`;
    let variant = sessionStorage.getItem(storageKey);
    if (!allowed.includes(variant)) {
        variant = pickWeighted(weights);
        sessionStorage.setItem(storageKey, variant);
    }

    params.set('experiments', mergeExperimentTag(existing, name, variant));
    const url = `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
    // Inertia keeps its page object (component, props, version) in
    // history.state; replacing it with anything else makes the next
    // back/forward popstate resolve "./Pages/undefined.vue".
    history.replaceState(nextHistoryState(history.state, url), '', url);

    return { variant };
}

/**
 * Variant forced by the URL's `experiments` param, or null if the param
 * carries no valid tag for this experiment.
 *
 * @param {string} existing Current `experiments` param value ('' if absent)
 * @param {string} name
 * @param {string[]} allowed
 * @returns {?string}
 */
export function variantFromUrlTags(existing, name, allowed) {
    const tag = splitTags(existing).find((t) => t.startsWith(`${name}:`));
    if (!tag) {
        return null;
    }
    const variant = tag.split(':')[1];

    return allowed.includes(variant) ? variant : null;
}

/**
 * New `experiments` param value with this experiment's tag replacing any
 * previous tag for the same experiment, other experiments untouched.
 *
 * @param {string} existing Current `experiments` param value ('' if absent)
 * @param {string} name
 * @param {string} variant
 * @returns {string}
 */
export function mergeExperimentTag(existing, name, variant) {
    const tags = splitTags(existing).filter((t) => !t.startsWith(`${name}:`));
    tags.push(`${name}:${variant}`);

    return tags.join('|');
}

/**
 * History state to pass to replaceState when only the URL changes: the
 * existing state with its url field updated, so Inertia's page object
 * survives. Null/undefined states pass through untouched — Inertia treats
 * a null popstate state as "reload from server", which is the safe path.
 *
 * @param {?Object} currentState
 * @param {string} url
 * @returns {?Object}
 */
export function nextHistoryState(currentState, url) {
    return currentState ? { ...currentState, url } : currentState;
}

function splitTags(existing) {
    return existing
        ? existing.split('|').map((s) => s.trim()).filter(Boolean)
        : [];
}

function pickWeighted(weights) {
    const total = Object.values(weights).reduce((a, b) => a + Number(b), 0);
    const keys = Object.keys(weights);
    if (total <= 0) {
        return keys[0];
    }
    let roll = Math.random() * total;
    for (const key of keys) {
        roll -= Number(weights[key]);
        if (roll <= 0) {
            return key;
        }
    }
    return keys[0];
}
